/**
 * Onboarding steps (SC-438). See the onboarding implementation plan in the vault.
 *
 * The step names and path orders are mirrored in `cloud/utils/onboardingSteps.js`, because cloud
 * code can't import types/. `server/__tests__/onboardingSteps.parity.test.ts` keeps the two in sync.
 */

export const ONBOARDING_STEPS = ['company', 'join', 'card', 'self', 'invite', 'walk'] as const

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]

/** The person who signs up and creates the office. */
export const CREATOR_PATH: readonly OnboardingStep[] = ['company', 'card', 'self', 'invite', 'walk']

/**
 * Someone who joins a company that already exists. Invitees start at `self`; a sign-up whose
 * email domain matched a company starts at `join`.
 */
export const MEMBER_PATH: readonly OnboardingStep[] = ['join', 'self', 'walk']

export type OnboardingPath = 'creator' | 'member'

/**
 * The person's company: the one a domain-matched person is joining (join screen), or the one the
 * creator just made (card and invite steps). Null before a company exists.
 */
export interface OnboardingCompany {
  id: string
  name: string
  memberCount: number
  /** The company has no free seat, so the person can't join it (and can't create another). */
  full: boolean
}

/** What `getOnboardingState` returns. */
export type OnboardingState =
  | { active: false }
  | {
      active: true
      step: OnboardingStep
      path: OnboardingPath
      company: OnboardingCompany | null
    }

export function pathSteps(path: OnboardingPath): readonly OnboardingStep[] {
  return path === 'creator' ? CREATOR_PATH : MEMBER_PATH
}
